import * as THREE from 'three';

export const ToadShader = {
    name: 'ToadShader',

    uniforms: {
        tDiffuse: { value: null },
        resolution: { value: new THREE.Vector2(1920, 1080) },

        // distortion + warping
        eyeBulge: { value: 0.1 },     // fisheye intensity
        sidePull: { value: 0.1 },     // frog-style horizontal stretch
        doubleVision: { value: 0.015 }, // binocular ghost

        // wet / blur
        corneaBlur: { value: 0.003 },

        // chromatic aberration
        chromaShift: { value: 0.003 },

        // vignette
        vignetteStrength: { value: 0.1 },

        // gamma
        gamma: { value: 0.5 },

        // animation (heartbeat, pulse, etc)
        time: { value: 0 },
    },

    vertexShader: /* glsl */ `
        varying vec2 vUv;
        void main() {
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
    `,

    fragmentShader: /* glsl */ `
        uniform sampler2D tDiffuse;
        uniform vec2 resolution;

        uniform float eyeBulge;
        uniform float sidePull;
        uniform float doubleVision;
        uniform float corneaBlur;
        uniform float chromaShift;

        uniform float gamma;
        uniform float vignetteStrength;

        uniform float time;

        varying vec2 vUv;

        void main() {

            vec2 uv = vUv;

            // -------------------------------------
            // 1. Frog fisheye lens (big round eyes)
            // -------------------------------------
            vec2 centered = uv * 2.0 - 1.0;
            float r = length(centered);
            float bulged = 1.0 + eyeBulge * r * r; 
            uv = centered * bulged * 0.5 + 0.5;

            // -------------------------------------
            // 2. Side pull — frogs see wide
            // -------------------------------------
            uv.x += (uv.x - 0.5) * sidePull * r;

            // -------------------------------------
            // 3. Chromatic aberration (frog eye)
            // -------------------------------------
            float cs = chromaShift * (0.5 + 0.5 * sin(time * 0.5));

            vec3 col;
            col.r = texture2D(tDiffuse, uv + vec2(cs, 0.0)).r;
            col.g = texture2D(tDiffuse, uv).g;
            col.b = texture2D(tDiffuse, uv - vec2(cs, 0.0)).b;

            // -------------------------------------
            // 4. Wet cornea blur
            // -------------------------------------
            vec3 blur = (
                texture2D(tDiffuse, uv + vec2( corneaBlur, 0)).rgb +
                texture2D(tDiffuse, uv + vec2(-corneaBlur, 0)).rgb +
                texture2D(tDiffuse, uv + vec2(0,  corneaBlur)).rgb +
                texture2D(tDiffuse, uv + vec2(0, -corneaBlur)).rgb
            ) * 0.25;

            col = mix(col, blur, 0.25);

            // -------------------------------------
            // 5. Double vision (binocular split)
            // -------------------------------------
            vec3 ghost = texture2D(tDiffuse, uv + vec2(doubleVision, 0.0)).rgb;
            col = mix(col, ghost, 0.5);

            // -------------------------------------
            // 6. Gamma correction
            // -------------------------------------
            col = pow(col, vec3(gamma));

            // -------------------------------------
            // 7. Vignette shaped like a frog eye
            // -------------------------------------
            float d = distance(vUv, vec2(0.5));
            col *= 1.0 - smoothstep(vignetteStrength * 0.5, 0.7, d);

            gl_FragColor = vec4(col, 1.0);
        }
    `,
};


export function createToadUniforms(
    resolution = new THREE.Vector2(1920, 1080)
) {
    return {
        tDiffuse: { value: null },
        resolution: { value: resolution.clone() },
        time: { value: 0 },

        eyeBulge: { value: 0.45 },
        sidePull: { value: 0.25 },
        doubleVision: { value: 0.015 },
        corneaBlur: { value: 0.003 },
        chromaShift: { value: 0.003 },

        vignetteStrength: { value: 0.8 },
    };
}
